import React from 'react'
import PropTypes from 'prop-types'
import { MdAddToPhotos } from 'react-icons/md'
import { Avatar, Skeleton, cn } from '@codecraftkit/theme'
import { size, frame, avatar, image as imageClass, overlay } from './styles'

const DEFAULT_IMG = 'https://dummyimage.com/900x900/000000/ffffff&text=+ATTACH+LOGO'

/**
 * The picture, with a "change it" layer over it.
 *
 * # The overlay is a `<button>`, and it is always in the DOM
 *
 * It was a `<Flex onClick>` mounted only while `isHoverImage` was true — a div, appearing on
 * hover. So changing the picture was mouse-only: there was nothing to tab to, because the thing
 * to tab to did not exist until the pointer was over it.
 *
 * A button that is always mounted and only PAINTED on hover keeps the hover look and gives the
 * keyboard something to reach. `group-hover:` does the painting, so the `isHoverImage` state
 * goes away with it.
 */
export const ProfileImageBaseAvatar = ({
  isRounded, image, urlField, item, nameField, noShadow, isDisabled, onOpen, isLoading, noBorder, className
}) => {
  if (isLoading) {
    return <Skeleton className={cn(size, isRounded ? 'rounded-full' : 'rounded-none', className)} />
  }

  const src = image || item?.[urlField] || DEFAULT_IMG
  const name = item?.[nameField] || 'Profile Image'

  return (
    <div className={cn('group', frame(isRounded, noShadow, noBorder), className)}>
      {isRounded
        ? <Avatar src={src} name={name} size='xl' className={cn(size, avatar(noShadow))} />
        : <img src={src} alt={name} className={cn(size, imageClass(noShadow))} />}

      {!isDisabled && (
        <button
          type='button'
          onClick={onOpen}
          aria-label='Change picture'
          className={cn(overlay(isRounded, noShadow), 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100')}
        >
          <MdAddToPhotos aria-hidden='true' className='w-6 h-6 text-white' />
        </button>
      )}
    </div>
  )
}

ProfileImageBaseAvatar.propTypes = {
  isRounded: PropTypes.bool,
  image: PropTypes.string,
  urlField: PropTypes.string,
  item: PropTypes.object,
  nameField: PropTypes.string,
  noShadow: PropTypes.bool,
  noBorder: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  onOpen: PropTypes.func,
  className: PropTypes.string
}
